import { FC, useCallback, useState } from "react";
import PreviewImage from "../PreviewImage";
import ModalColserAutoPeritaje from "../ModalColserAutoPeritaje";
import ModalCertificadoPeritajeAutomas from "../../../../../components/certificadoPeritajeAutomas/ModalCertificadoPeritajeAutomas";

interface ModalPeritajeVehiculoProps {
    plate: string;
    /** PDF de peritaje cargado directamente en el vehículo. */
    automasPdf?: string | null;
    /** Id del peritaje de Colserauto, cuando el vehículo lo trae. */
    idColserauto?: string | null;
    close: () => void;
}

/** Fuente que se está intentando mostrar en este momento. */
type Fuente = "automas" | "colserautoPdf" | "colserautoPlaca";

/**
 * Peritaje del vehículo, con prioridad Automás → Colserauto.
 *
 * 1. Si el vehículo trae su propio PDF (`automas_pdf`) se muestra ese documento y no se consulta nada.
 * 2. Si no, se consulta el certificado de Automás por placa (`get-car-automas/<placa>/`).
 * 3. Solo si Automás no tiene peritaje se pasa a Colserauto: el PDF por `id_colserauto` cuando el
 *    vehículo lo trae, y si no (o si ese PDF falla) la consulta por placa.
 * 4. Si Colserauto tampoco tiene, su modal muestra la pantalla de "no encontrado".
 */
const ModalPeritajeVehiculo: FC<ModalPeritajeVehiculoProps> = ({ plate, automasPdf, idColserauto, close }) => {
    const [fuente, setFuente] = useState<Fuente>("automas");

    // Estables: los modales las usan como dependencia del efecto que consulta la API.
    const pasarAColserauto = useCallback(
        () => setFuente(idColserauto ? "colserautoPdf" : "colserautoPlaca"),
        [idColserauto],
    );
    const pasarAColserautoPorPlaca = useCallback(() => setFuente("colserautoPlaca"), []);

    // El PDF propio del vehículo ya es un peritaje resuelto: no hay nada que consultar.
    if (automasPdf) {
        return <PreviewImage automasPdf={automasPdf} plate={plate} close={close} />;
    }

    if (fuente === "automas") {
        return <ModalCertificadoPeritajeAutomas plate={plate} close={close} onNoData={pasarAColserauto} />;
    }

    if (fuente === "colserautoPdf") {
        return (
            <PreviewImage
                idColserauto={idColserauto as string}
                plate={plate}
                close={close}
                onNoData={pasarAColserautoPorPlaca}
            />
        );
    }

    return <ModalColserAutoPeritaje placa={plate} close={close} />;
};

export default ModalPeritajeVehiculo;
